import React from 'react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Cookie, ShieldCheck, BarChart3, Target, Settings, Info, Mail } from 'lucide-react';

const CookiePolicyPage: React.FC = () => {
  const lastUpdated = "January 5, 2026";
  const companyName = "RunOffTrack.com";
  const contactEmail = "danielgaskins99@gmail.com";

  const sections = [
    { id: 'definition', label: '1. What are Cookies?' },
    { id: 'purpose', label: '2. Why we use them' },
    { id: 'types', label: '3. Types of Cookies' },
    { id: 'third-party', label: '4. Third-Parties' },
    { id: 'control', label: '5. Your Controls' },
    { id: 'dnt', label: '6. Do Not Track' },
    { id: 'updates', label: '7. Policy Updates' },
    { id: 'contact', label: '8. Contact Us' },
  ];

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO 
        title="Cookie Policy - RunOffTrack.com"
        description="Learn how RunOffTrack.com uses cookies and similar technologies to provide, customize, and improve our services."
        canonical="/cookie-policy"
      />

      {/* Hero Section */}
      <div className="bg-muted/30 pt-32 pb-20 border-b border-border">
        <div className="container px-4 text-center max-w-4xl mx-auto">
           <Breadcrumbs items={[{ label: 'Legal', href: '/privacy-policy' }, { label: 'Cookie Policy', href: '/cookie-policy' }]} />
           
           <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-full mb-6">
             <Cookie size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-foreground">
             Cookie Policy
           </h1>
           <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
             Transparency is the core of compliance. This policy details how we use technologies to enhance your experience at {companyName}.
           </p>
           <div className="mt-6 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
             Last Updated: {lastUpdated}
           </div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[250px_1fr] gap-16 items-start">
          
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block sticky top-24 space-y-4 border-r border-border pr-8">
            <p className="text-xs font-bold uppercase text-muted-foreground tracking-tighter mb-6">On this page</p>
            <nav className="flex flex-col space-y-3">
              {sections.map(s => (
                <a 
                  key={s.id} 
                  href={`#${s.id}`} 
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <article className="prose prose-slate dark:prose-invert max-w-none space-y-16">
            
            <section id="definition" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg"><Info size={20}/></div>
                1. What are Cookies?
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small data files that are placed on your computer or mobile device when you visit a website. 
                Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, 
                as well as to provide reporting information.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="p-4 bg-muted/30 rounded-xl border border-border">
                  <p className="font-bold text-sm mb-1">First-party cookies</p>
                  <p className="text-xs text-muted-foreground">Cookies set directly by RunOffTrack.com.</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-xl border border-border">
                  <p className="font-bold text-sm mb-1">Third-party cookies</p>
                  <p className="text-xs text-muted-foreground">Cookies set by parties other than us (e.g. Stripe, Google).</p>
                </div>
              </div>
            </section>

            <Separator />

            <section id="purpose" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg"><Settings size={20}/></div>
                2. Why do we use Cookies?
              </h2>
              <p className="text-muted-foreground">
                We use first-party and third-party cookies for several reasons. Some cookies are required for technical 
                reasons in order for our Online Properties to operate, and we refer to these as "essential" or 
                "strictly necessary" cookies. Other cookies enable us to track and target the interests of our users 
                to enhance the experience on our Website and Application.
              </p>
            </section>

            <section id="types" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-8">3. Types of Cookies we use</h2>
              
              <div className="space-y-6">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <ShieldCheck className="text-blue-500 shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-bold text-lg">Strictly Necessary Cookies</h4>
                        <p className="text-sm text-muted-foreground">
                          Essential for the Website to function. They handle authentication, security, and session management.
                          <br/><span className="italic font-medium">Opt-out status: Not available.</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Settings className="text-orange-500 shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-bold text-lg">Functional & Performance Cookies</h4>
                        <p className="text-sm text-muted-foreground">
                          Used to remember your preferences (like Dark Mode) and improve the performance of our tools.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <BarChart3 className="text-green-500 shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-bold text-lg">Analytics & Customization Cookies</h4>
                        <p className="text-sm text-muted-foreground">
                          Help us understand how the platform is used and how effective our help guides are. 
                          We use Google Analytics 4 (GA4) for this purpose.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Target className="text-red-500 shrink-0 mt-1" size={24} />
                      <div>
                        <h4 className="font-bold text-lg">Advertising (Targeting) Cookies</h4>
                        <p className="text-sm text-muted-foreground">
                          Used to make marketing messages more relevant to you on platforms like Facebook, Reddit, and TikTok.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            <Separator />

            <section id="third-party" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                4. Third-Party Cookies
              </h2>
              <p className="text-muted-foreground">
                Our service integrates with several third-party providers who may set cookies to enable their specific functionality:
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm font-medium">
                <li>• Google (Auth, Maps, Analytics)</li>
                <li>• Stripe (Payments & Fraud Prevention)</li>
                <li>• SendGrid (Email Deliverability)</li>
                <li>• Open-Meteo (Rainfall Data)</li>
              </ul>
            </section>

            <section id="control" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                5. How can I control Cookies?
              </h2>
              <p className="text-muted-foreground">
                You have the right to decide whether to accept or reject non-essential cookies.
              </p>
              <div className="bg-primary/5 p-6 rounded-2xl border border-primary/10">
                <ol className="space-y-4 m-0 list-none">
                  <li className="flex gap-4">
                    <div className="font-bold text-primary">01.</div>
                    <p className="text-sm m-0"><strong>Our Consent Manager:</strong> Use the "Cookie Preferences" link in our footer to update your settings at any time.</p>
                  </li>
                  <li className="flex gap-4">
                    <div className="font-bold text-primary">02.</div>
                    <p className="text-sm m-0"><strong>Browser Controls:</strong> You can set your browser to refuse all cookies. Visit your browser’s "Help" section for instructions.</p>
                  </li>
                </ol>
              </div>
            </section>

            <section id="dnt" className="scroll-mt-24">
              <h2 className="text-xl font-bold">6. Do Not Track (DNT)</h2>
              <p className="text-sm text-muted-foreground italic">
                Standardized DNT technology is currently in development. RunOffTrack.com does not currently respond to DNT signals.
              </p>
            </section>

            <section id="contact" className="scroll-mt-24 pt-12">
              <div className="bg-slate-900 text-white p-8 rounded-3xl text-center">
                 <Mail className="mx-auto mb-4 text-primary" size={32} />
                 <h3 className="text-2xl font-bold mb-2">Questions?</h3>
                 <p className="text-slate-400 mb-6">If you have any questions about our use of cookies, please contact us.</p>
                 <a 
                    href={`mailto:${contactEmail}`} 
                    className="inline-block bg-primary text-white font-bold py-3 px-8 rounded-full hover:bg-primary/90 transition-colors"
                  >
                   {contactEmail}
                 </a>
              </div>
            </section>

          </article>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
